import { Alert, Box, Button, ButtonGroup, FormActions, Heading, Modal, TextButton } from '@zillow/constellation-10';
import { IconPlusFilled } from '@zillow/constellation-icons';
import {
  CHOOSE_CUSTOM_REPLY_CLICK_EVENT_TYPE_ID,
  emitChooseCustomReplyClickEvent,
  emitChooseDefaultReplyClickEvent,
} from '../../../constants/clickstreamEvents/savedReplyModal';
import { createSavedReplyTextStore } from '../../../lib/inboxStores';
import { addMessageInputTextToAppendStore, isAddMessageInputFocusedStore } from '../../../lib/inboxAddMessageStores';
import type { TriggerLocation } from '../../../constants/clickstreamEvents/globals';
import useModal from '../../../hooks/useModal';
import { MAX_SAVED_REPLIES_COUNT, useSavedReplies } from '../../../hooks/useSavedReplies';
import { SAVED_REPLY_MESSAGE_MAX_LENGTH, SAVED_REPLY_TITLE_MAX_LENGTH } from '../../../constants/ConfigConstants';
import formValidationUtils from '../../../utils/formValidationUtils';
import isDefaultSavedReply from '../../../utils/isDefaultSavedReply';
import SavedReplyEditFields from './components/SavedReplyEditFields';
import SavedReplyList from './components/SavedReplyList';
import Skeleton from './Skeleton';
import { useAuth } from '../../../AuthProvider';

type SavedReplyModalProps = {
  listingAlias: string;
  leadId?: string | null;
  triggerLocation: TriggerLocation;
};

export default function SavedReplyModal({ listingAlias, leadId, triggerLocation }: SavedReplyModalProps): JSX.Element {
  const { clearModal } = useModal();
  const { user } = useAuth();
  const {
    editedSavedReply,
    handleCancel,
    handleCreate,
    handleDelete,
    handleEdit,
    handleSave,
    handleSelect,
    hasErrorLoadingSavedReplies,
    hasFormError,
    hasReachedMaxCustomRepliesLimit,
    hasDuplicateTitle,
    isLoadingSavedReplies,
    isDeletingReply,
    isSavingReply,
    mode,
    savedReplies,
    selectedSavedReply,
    setEditedSavedReply,
  } = useSavedReplies({ listingAlias, leadId, triggerLocation });

  const isDefaultReplySelected = isDefaultSavedReply(selectedSavedReply);
  const shouldShowLoadingSkeleton = isLoadingSavedReplies || isSavingReply;
  const shouldDisableSave =
    formValidationUtils.isInputEmpty(editedSavedReply.title) ||
    formValidationUtils.isInputEmpty(editedSavedReply.reply) ||
    hasDuplicateTitle(editedSavedReply.title) ||
    editedSavedReply.title.length > SAVED_REPLY_TITLE_MAX_LENGTH ||
    editedSavedReply.reply.length > SAVED_REPLY_MESSAGE_MAX_LENGTH;
  const shouldDisableChoose = shouldShowLoadingSkeleton || hasErrorLoadingSavedReplies;
  const shouldDisableCreate =
    hasReachedMaxCustomRepliesLimit || shouldShowLoadingSkeleton || hasErrorLoadingSavedReplies;

  const handleChooseClick = (): void => {
    if (isDefaultReplySelected) {
      emitChooseDefaultReplyClickEvent({ user, listing: { listingAlias }, triggerLocation, lead: { leadId } });
    } else {
      emitChooseCustomReplyClickEvent({ user, listing: { listingAlias }, triggerLocation, lead: { leadId } });
    }

    addMessageInputTextToAppendStore.set(selectedSavedReply.reply);
    isAddMessageInputFocusedStore.set(true);
    clearModal();
  };

  return (
    <Modal
      css={{ overflowX: 'hidden' }}
      data-testid="saved-reply-modal"
      dividers={false}
      open={true}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          clearModal();
          createSavedReplyTextStore.set('');
        }
      }}
      scrollDocument={false}
      shouldAwaitInteractionResponse
      size={{
        base: 'fullScreen',
        lg: 'md',
      }}
      header={
        <Heading level={1} textStyle="heading-xs" data-testid="saved-reply-modal-header">
          Saved replies
        </Heading>
      }
      body={(() => {
        if (mode !== 'view') {
          return (
            <SavedReplyEditFields
              editedSavedReply={editedSavedReply}
              setEditedSavedReply={setEditedSavedReply}
              hasFormError={hasFormError}
              hasDuplicateTitle={hasDuplicateTitle}
            />
          );
        }

        if (shouldShowLoadingSkeleton) {
          return <Skeleton />;
        }

        return (
          <>
            <Box
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingTop: 'default',
                paddingBottom: 'loose',
                position: 'sticky',
                top: '-17px',
                zIndex: 1,
                backgroundColor: 'bg.neutral',
              }}
            >
              <Heading level={2} textStyle="heading-xs" data-testid="my-saved-replies">
                My replies
              </Heading>
              <TextButton
                disabled={shouldDisableCreate}
                onClick={handleCreate}
                icon={<IconPlusFilled />}
                data-testid="saved-reply-new-btn"
              >
                New saved reply
              </TextButton>
            </Box>

            {hasReachedMaxCustomRepliesLimit && (
              <Alert css={{ marginBottom: 'loose' }} role="status" tone="warning">
                You have reached the maximum limit of {MAX_SAVED_REPLIES_COUNT} saved replies. To add more, please
                delete some of your infrequently used replies.
              </Alert>
            )}

            {hasErrorLoadingSavedReplies ? (
              <Alert tone="critical" css={{ marginBottom: 'loose' }}>
                Sorry, we are unable to load saved replies. Please try refreshing the page.
              </Alert>
            ) : (
              <SavedReplyList
                activeReplyIndex={selectedSavedReply.replyIndex}
                handleDelete={handleDelete}
                handleEdit={handleEdit}
                handleSelect={handleSelect}
                isDeletingReply={isDeletingReply}
                savedReplies={savedReplies}
                shouldDisableEditDefaultReply={shouldDisableCreate && isDefaultReplySelected}
              />
            )}
          </>
        );
      })()}
      footer={(() => {
        if (mode === 'view') {
          return (
            <Button
              data-eventid={CHOOSE_CUSTOM_REPLY_CLICK_EVENT_TYPE_ID}
              emphasis="filled"
              fluid
              tone="brand"
              disabled={shouldDisableChoose}
              onClick={handleChooseClick}
              data-testid="saved-reply-choose-btn"
            >
              Choose reply
            </Button>
          );
        }

        return (
          <FormActions asChild>
            <ButtonGroup aria-label="Edit saved reply actions" fluid>
              <TextButton onClick={handleCancel}>Cancel</TextButton>
              <Button
                emphasis="filled"
                tone="brand"
                disabled={shouldDisableSave}
                onClick={handleSave}
                loading={isSavingReply}
              >
                Save
              </Button>
            </ButtonGroup>
          </FormActions>
        );
      })()}
    />
  );
}
